/**
 * O QUE ESTÁ ESPERANDO — e como tirar dali.
 *
 * Esta tela nasceu de um tropeço real. O gestor clicou várias vezes no botão
 * de sacos esperando ver alguma coisa mudar de cor, e cada clique virou um
 * trato. Quando percebeu, o contador estava alto e não havia por onde mexer:
 * os registros estavam guardados no aparelho, intactos, e invisíveis.
 *
 * Duas lições viraram código aqui:
 *
 * 1. FILA QUE NÃO SE ABRE É CAIXA-PRETA. Um número sozinho diz que há coisa
 *    guardada e não diz o quê. Quem vai apertar Sincronizar precisa poder
 *    conferir antes — ainda mais porque depois de subir, desfazer deixa de ser
 *    grátis e passa a exigir um lançamento que anula.
 *
 * 2. ENQUANTO ESTÁ AQUI, APAGAR É APAGAR. O registro nunca existiu para o
 *    banco, então sumir com ele não deixa buraco nem rastro. É a única janela
 *    em que isso é verdade, e ela fecha no Sincronizar. Por isso o botão de
 *    desfazer mora aqui e não lá.
 */

import { useState } from 'react'
import { descartar, descartarTudo, pendentes, type Pendente } from '../acesso/fila'

export const NOME: Record<Pendente['tipo'], string> = {
  trato: 'trato',
  leitura: 'leitura',
  falta: 'falta',
  biometria: 'biometria',
  despesca: 'despesca',
  movimentacao: 'movimentação',
  recepcao: 'recepção',
  aplicacao: 'aplicação',
  perda: 'perda',
  anular_trato: 'anular trato',
  mov_racao: 'ração no galpão',
  fechar_pilha: 'pilha fechada',
  anular_fechamento: 'desfazer fechamento',
  anular_mov_racao: 'desfazer lançamento de ração',
  remessa: 'remessa de peixe',
  encerrar_ciclo: 'encerrar ciclo',
  anular_remessa: 'desfazer remessa',
  nota_racao: 'preço da nota de ração',
}

export function Fila({ aoMudar }: { aoMudar: () => void }) {
  const [confirmando, setConfirmando] = useState(false)
  const lista = pendentes()

  if (lista.length === 0) {
    return <p className="fila-vazia">Nada esperando. Tudo que foi registrado já subiu.</p>
  }

  return (
    <div className="fila">
      <p className="secundario">
        {lista.length} {lista.length === 1 ? 'registro guardado' : 'registros guardados'} no
        aparelho. Confira antes de sincronizar — depois de subir, desfazer deixa de ser só
        apagar.
      </p>

      <ul className="fila-lista">
        {lista.map((p) => (
          <li key={p.id} className={p.recusa ? 'fila-item recusado' : 'fila-item'}>
            {/* Endereçado: O QUÊ, ONDE, QUANTO, QUANDO — nessa ordem.
                Antes vinha o tanque primeiro e o tipo em cinza miúdo, e a
                linha não respondia sozinha o que era aquele registro. */}
            <div className="fila-texto">
              <strong>
                {NOME[p.tipo]} {p.rotulo ?? ''}
              </strong>
              <div className="secundario">
                {p.resumo ? `${p.resumo} · ` : ''}
                {diaCurto(p.dia)}
              </div>
              {p.recusa && <div className="fila-recusa">o servidor recusou: {p.recusa}</div>}
              {/* A FALHA DE CONEXÃO NO ITEM, em ocre e não em vinho: pode ser só
                  o sinal. Repetida, aponta o registro que trava a fila. */}
              {p.falha && (
                <div className="fila-falha">
                  não subiu: {p.falha}
                  {(p.falhas_seguidas ?? 0) >= 2 && <> — {p.falhas_seguidas} vezes seguidas</>}
                </div>
              )}
            </div>
            <button
              type="button"
              className="discreto"
              onClick={() => {
                descartar(p.id)
                aoMudar()
              }}
            >
              desfazer
            </button>
          </li>
        ))}
      </ul>

      {/* O apagar tudo existe para o caso que criou esta tela: erro em série.
          Ele confirma porque é o único botão do app que some com mais de uma
          coisa de uma vez, e dizer o número na pergunta é o que impede que
          alguém confirme no automático. */}
      {confirmando ? (
        <div className="fila-confirmar">
          <span>Apagar os {lista.length} registros que estão esperando?</span>
          <button
            type="button"
            className="botao-perigo"
            onClick={() => {
              descartarTudo()
              setConfirmando(false)
              aoMudar()
            }}
          >
            Apagar tudo
          </button>
          <button type="button" className="discreto" onClick={() => setConfirmando(false)}>
            não
          </button>
        </div>
      ) : (
        <button type="button" className="discreto" onClick={() => setConfirmando(true)}>
          apagar tudo
        </button>
      )}
    </div>
  )
}

function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}
